'use client'

import { ArrowLeft24 } from '@vinted/monochrome-icons'
import { Button, Divider, Icon, Navigation, Spacer, Text } from '@vinted/web-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterNavigationalOptions,
  DynamicFilterOptionModel,
  ExperimentModel,
  FilterCatalogModel,
  GRID_FILTER_DROPDOWN_MAX_HEIGHT,
  GRID_FILTER_DROPDOWN_MIN_WIDTH,
  Filter,
  DynamicExposeType,
  OptionType,
  trackExperimentsByType,
} from '@marketplace-web/catalog/catalog-data'
import { useCatalogContext, clickFilterEvent } from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'

import FilterCatalogList from '../../../FilterCatalogList/FilterCatalogList'
import FilterDropdown from '../../../FilterDropdown'
import VerticalPadder from '../Common/VerticalPadder'
import GridGroup from '../Common/GridGroup'

type Props = {
  type: string
  title: string
  isSelectionHighlighted: boolean
  rootOptionType: OptionType
  isHidden?: boolean
  experiments?: Array<ExperimentModel>
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  fromScreen: string
  onListOptionSelect: (id: number) => void
}

const GridFilterDropdown = ({
  options,
  rootOptionType,
  selectedIds,
  isHidden,
  experiments,
  title,
  type,
  isSelectionHighlighted,
  fromScreen,
  onListOptionSelect,
}: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const trackAbTest = useTrackAbTestCallback()
  const {
    state: {
      items: { searchTrackingParams },
    },
  } = useCatalogContext()

  const [selectedCatalogCode, setSelectedCatalogCode] = useState('')
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)

  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId

  const seenItems = useRef(new Set<number>())

  useEffect(() => {
    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.View, trackAbTest)
  }, [trackAbTest, experiments])

  useEffect(() => {
    if (rootOptionType === OptionType.Navigational) {
      setSelectedCatalogCode('')

      return
    }

    setSelectedCatalogCode(OptionType.Root)
  }, [rootOptionType])

  const filteringSessionId = useMemo(() => uuid(), [])

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const navigationalItems = useMemo(() => {
    return transformDynamicFilterNavigationalOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const navigatedRoot = useMemo(() => {
    return options.find(item => selectedCatalogCode === `${item.type}_${item.id}`)
  }, [options, selectedCatalogCode])

  const navigatedGroups = useMemo(() => {
    if (selectedCatalogCode === OptionType.Root) return options

    return navigatedRoot?.options || []
  }, [navigatedRoot?.options, options, selectedCatalogCode])

  const navigatedGroupsSelected = useMemo(() => {
    return navigatedGroups
      ?.map(item => ({
        ...item,
        options: item.options.filter(option => highlightedSelectedIds.includes(option.id)),
      }))
      .filter(item => item.options.length > 0)
  }, [highlightedSelectedIds, navigatedGroups])

  const groupToCount = useMemo(() => {
    const map = new Map<number, number>()

    navigatedGroups?.forEach(group => {
      let count = 0

      group.options.forEach(item => {
        if (selectedIds.includes(item.id)) {
          count += 1
        }
      })

      map.set(group.id, count)
    })

    return map
  }, [navigatedGroups, selectedIds])

  const getPosition = (id: number) => flattedOptions.findIndex(item => item.id === id) + 1

  const handleNavClick = (catalog: FilterCatalogModel) => {
    setSelectedCatalogCode(catalog.code)
  }

  const handleNavBackClick = () => {
    setSelectedCatalogCode('')
    setHighlightedSelectedIds(selectedIds)
  }

  const handleClose = () => {
    seenItems.current.clear()
    setHighlightedSelectedIds(selectedIds)
  }

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Dynamic,
        screen: 'search_filter',
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )
    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Click, trackAbTest)
  }

  const handleSelect = (item: DynamicFilterOptionModel) => {
    onListOptionSelect(item.id)

    track(
      userClickDynamicFilterOptionEvent({
        position: getPosition(item.id),
        // negating here because state updates are async
        isSelected: !selectedIds.includes(item.id),
        id: item.id,
        itemsCount: item.itemsCount,
        type,
        code: type,
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Apply, trackAbTest)
  }

  const handleInViewItem = (item: DynamicFilterOptionModel) => {
    if (seenItems.current.has(item.id)) return
    seenItems.current.add(item.id)

    track(
      userViewDynamicFilterOptionEvent({
        position: getPosition(item.id),
        id: item.id,
        itemsCount: item.itemsCount,
        type,
        code: type,
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const renderNavigation = () => {
    if (!navigatedRoot) {
      return undefined
    }

    return (
      <>
        <Navigation
          body={navigatedRoot.title}
          left={
            <Button
              styling={Button.Styling.Flat}
              theme="amplified"
              icon={<Icon name={ArrowLeft24} />}
              onClick={handleNavBackClick}
              testId={`${type}-dropdown-go-back`}
              aria={{
                'aria-label': translate('catalog.filters.a11y.actions.back'),
              }}
            />
          }
        />
        <Divider />
      </>
    )
  }

  const renderHeader = () => {
    if (!selectedCatalogCode) return undefined
    if ((navigatedGroupsSelected?.length || 0) < 1) return undefined
    if (!isSelectionHighlighted) return undefined

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <VerticalPadder>
          <Text
            as="h3"
            text={translate('catalog.filters.dynamic.applied_filters')}
            type={Text.Type.Subtitle}
          />
        </VerticalPadder>
        {navigatedGroupsSelected?.map(gridItem => (
          <GridGroup
            key={gridItem.id}
            option={gridItem}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onInViewItem={handleInViewItem}
          />
        ))}
        <Spacer size={Spacer.Size.Large} />
        <Divider />
      </>
    )
  }

  const renderDropdown = () => {
    if (!selectedCatalogCode) {
      return <FilterCatalogList catalogs={navigationalItems} onItemClick={handleNavClick} />
    }

    return navigatedGroups?.map(gridItem => (
      <GridGroup
        key={gridItem.id}
        isSticky
        selectedCount={groupToCount.get(gridItem.id)}
        option={gridItem}
        selectedIds={selectedIds}
        onSelect={handleSelect}
        onInViewItem={handleInViewItem}
      />
    ))
  }

  if (isHidden) return null

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={title}
        onClose={handleClose}
        onFilterClick={handleOnFilterClick}
        minWidth={GRID_FILTER_DROPDOWN_MIN_WIDTH}
        testId={`catalog--${type}-filter`}
        areAnyOfItemsSelected={selectedIds.length > 0}
        useChipAsTrigger
      >
        <div className="u-ui-padding-vertical-x-small">
          {renderNavigation()}
          <ScrollableArea maxHeight={GRID_FILTER_DROPDOWN_MAX_HEIGHT}>
            {renderHeader()}
            {renderDropdown()}

            {selectedCatalogCode && <Spacer size={Spacer.Size.Large} />}
          </ScrollableArea>
        </div>
      </FilterDropdown>
    </div>
  )
}

export default GridFilterDropdown
